import { Box, Fab } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { ALPHA, BADGE, INK_BLACK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { SystemTabBadgeIndicator } from "../Badges/SystemTabBadgeIndicator";
import { useDraggableFabPosition } from "./useDraggableFabPosition";

/** `Brand` fills the disc with the primary color for a glyph; `Plain` is a white disc for a full-color illustration (an avatar). */
export enum ChatLauncherSurface {
	Brand = "brand",
	Plain = "plain",
}

type Props = {
	onClick: () => void;
	ariaLabel: string;
	tooltip: string;
	/** The FAB glyph — an icon or an <img>. */
	icon: ReactNode;
	/** Disc fill behind the glyph. Default "brand". */
	surface?: ChatLauncherSurface;
	/** FAB diameter (px). Default 56. */
	size?: number;
	/** Fixed offset from the viewport bottom (px). Default 100 — agrees with FloatingChatWindow's anchor. */
	bottom?: number;
	/** Fixed offset from the viewport right (px). Default 50 — agrees with FloatingChatWindow's anchor. */
	right?: number;
	zIndex?: number;
	/** Let the user drag the FAB anywhere in the viewport. Requires `dragStorageKey` to remember where. */
	draggable?: boolean;
	/** Storage key the dropped position is saved under (per user). */
	dragStorageKey?: string;
	/**
	 * Replies that arrived while the chat was closed, shown as a count on the top-left edge in every direction — the
	 * top-right stays free for a badge the icon draws itself (e.g. `AssistantAvatar`'s count); 0 shows nothing.
	 */
	// DEFAULT: absent draws no count, as every launcher looked before this prop existed.
	unreadCount?: number;
	dataTestId?: string;
};

// Geometry defaults — all prop-overridable per instance (that IS the design-system dial here).
// bottom/right agree with FloatingChatWindow's anchor so the window rises out of this FAB.
const DEFAULT_SIZE = 56;
// DEFAULT: brand is the look every launcher had before `surface` existed.
const DEFAULT_SURFACE = ChatLauncherSurface.Brand;

const SURFACE_SX = {
	[ChatLauncherSurface.Brand]: { bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } },
	[ChatLauncherSurface.Plain]: { bgcolor: SURFACE, color: "text.primary", "&:hover": { bgcolor: NEUTRAL[100] } },
} as const satisfies Record<ChatLauncherSurface, object>;
const DEFAULT_BOTTOM = 100;
const DEFAULT_RIGHT = 50;
const DEFAULT_Z_INDEX = 1300;

const NO_UNREAD = 0;

/** Inset that centres the count on the disc's edge at 45°, so it sits on the circle instead of floating off its corner. */
const unreadCountInset = (size: number): number => (size / 2) * (1 - Math.SQRT1_2) - BADGE.PILL_SIZE / 2;

/**
 * The launcher FAB for a FloatingChatWindow: a fixed bottom-right brand button
 * that opens the chat window, wrapped in a tooltip. Position defaults agree with
 * FloatingChatWindow's anchor so the window appears to rise out of the FAB.
 * Optionally draggable — the button then moves anywhere in the viewport and stays
 * where the user dropped it, while the chat window keeps its own anchor.
 */
export const ChatLauncherFab: FC<Props> = ({
	onClick,
	ariaLabel,
	tooltip,
	icon,
	surface = DEFAULT_SURFACE,
	size = DEFAULT_SIZE,
	bottom = DEFAULT_BOTTOM,
	right = DEFAULT_RIGHT,
	zIndex = DEFAULT_Z_INDEX,
	draggable = false,
	dragStorageKey,
	unreadCount = NO_UNREAD,
	dataTestId,
}) => {
	const { offset, isDragging, dragHandlers, consumeDragClick } = useDraggableFabPosition({
		enabled: draggable,
		storageKey: dragStorageKey,
		size,
		bottom,
		right,
	});

	const handleClick = () => {
		// Releasing the pointer after a drag also fires a click on the FAB — swallow that one.
		if (consumeDragClick()) return;
		onClick();
	};

	return (
		// A tooltip trailing the button mid-drag is noise. An empty title renders no tooltip at all,
		// which beats fighting the (controlled-open) tooltip's hover state; it returns on drop.
		<InHouseTooltip title={isDragging ? "" : tooltip}>
			<Fab
				data-testid={dataTestId}
				aria-label={ariaLabel}
				onClick={handleClick}
				{...(draggable ? dragHandlers : {})}
				// Inline, not `sx`: the offset changes on every pointer move, and emotion would mint
				// (and inject) a fresh class for each pixel of the drag.
				style={draggable ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined}
				sx={{
					position: "fixed",
					bottom,
					right,
					zIndex,
					width: `${size}px`,
					height: `${size}px`,
					...SURFACE_SX[surface],
					// No SHADOW.* token matches a launcher-FAB elevation (PANEL_SOFT is too flat,
					// FLOATING_WINDOW too heavy); keep the neutral-ink alpha lift local to the FAB.
					boxShadow: `0 4px 12px ${alpha(INK_BLACK, ALPHA.SHADOW_HEAVY)}`,
					...(draggable && {
						cursor: isDragging ? "grabbing" : "grab",
						// Without this a drag that starts on the glyph selects it instead of moving the FAB.
						userSelect: "none",
						// The drag owns the pointer on touch, so the page must not scroll under it.
						touchAction: "none",
					}),
				}}
			>
				{icon}
				{unreadCount > NO_UNREAD && (
					<Box
						component="span"
						aria-hidden
						data-testid={dataTestId === undefined ? undefined : `${dataTestId}-unread-count`}
						sx={{
							position: "absolute",
							top: unreadCountInset(size),
							left: unreadCountInset(size),
							display: "flex",
							borderRadius: BADGE.PILL_RADIUS,
							boxShadow: `0 0 0 ${BADGE.DOT_RING_PX}px ${SURFACE}`,
						}}
					>
						<SystemTabBadgeIndicator badge={{ content: unreadCount, color: "primary" }} />
					</Box>
				)}
			</Fab>
		</InHouseTooltip>
	);
};
